/**
 * Workspace Artifact Types
 *
 * Artifacts are work products created by the user or agent:
 * - Documents (rich text, markdown)
 * - Email drafts
 * - Reports (with visualizations)
 * - Data views (tables)
 */

import type { AgentCitation } from '../../../api/services/agent';

// Base artifact interface
export interface BaseArtifact {
  id: string;
  type: ArtifactType;
  title: string;
  createdAt: string;
  updatedAt: string;
  createdBy: 'user' | 'agent';
  // Which agent message created/updated this artifact
  sourceMessageId?: string;
  // Citations used to create this artifact
  citations?: AgentCitation[];
  // Artifact status
  status: 'draft' | 'ready' | 'sent' | 'archived';
  // Access level: 'owner' | 'edit' | 'view' | undefined (undefined means owned)
  access_level?: 'owner' | 'edit' | 'view';
}

export type ArtifactType = 'document' | 'email' | 'report' | 'data';

export type DocumentFormat = 'markdown' | 'richtext' | 'tiptap_json';

// Document artifact - rich text content (HTML or TipTap JSON)
export interface DocumentArtifact extends BaseArtifact {
  type: 'document';
  // For legacy documents this is an HTML string; for new OCR documents and
  // JSON-native documents this is a TipTap/ProseMirror doc object.
  content: string | Record<string, unknown>;
  // kept for backward compat; new docs prefer 'tiptap_json' when available
  format: DocumentFormat;
  sourceOcrId?: string;
  sourceFilename?: string;
}

// Email artifact - structured email draft
export interface EmailArtifact extends BaseArtifact {
  type: 'email';
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  body: string; // HTML or plain text
  bodyFormat: 'html' | 'plain';
  attachments?: EmailAttachment[];
}

export interface EmailAttachment {
  id: string;
  name: string;
  size: number;
  type: string;
  url?: string;
}

// Report artifact - document with embedded visualizations
export interface ReportArtifact extends BaseArtifact {
  type: 'report';
  sections: ReportSection[];
}

export interface ReportSection {
  id: string;
  type: 'text' | 'chart' | 'table' | 'metric';
  title?: string;
  content: TextContent | ChartContent | TableContent | MetricContent;
}

export interface TextContent {
  type: 'text';
  markdown: string;
}

export interface ChartContent {
  type: 'chart';
  chartType: 'bar' | 'line' | 'pie' | 'donut' | 'area';
  data: ChartDataPoint[];
  options?: Record<string, unknown>;
}

export interface ChartDataPoint {
  label: string;
  value: number;
  color?: string;
}

export interface TableContent {
  type: 'table';
  columns: TableColumn[];
  rows: Record<string, unknown>[];
}

export interface TableColumn {
  key: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'currency';
  format?: string;
}

export interface MetricContent {
  type: 'metric';
  value: number | string;
  label: string;
  change?: number; // Percentage change
  changeDirection?: 'up' | 'down' | 'neutral';
  format?: 'number' | 'currency' | 'percent';
}

// Data artifact - tabular data view
export interface DataArtifact extends BaseArtifact {
  type: 'data';
  columns: TableColumn[];
  rows: Record<string, unknown>[];
  sourceFile?: string;
}

// Union type for all artifacts
export type Artifact = DocumentArtifact | EmailArtifact | ReportArtifact | DataArtifact;

// Workspace state
export interface WorkspaceArtifactState {
  artifacts: Artifact[];
  activeArtifactId: string | null;
  // Tabs order
  openArtifactIds: string[];
}

// Helper to generate artifact IDs
const generateArtifactId = () => `artifact-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Helper to create base artifact fields
const createBaseFields = (
  type: ArtifactType,
  title: string,
  createdBy: 'user' | 'agent',
): Omit<BaseArtifact, 'status'> & { status: BaseArtifact['status'] } => {
  const now = new Date().toISOString();
  return {
    id: generateArtifactId(),
    type,
    title,
    createdAt: now,
    updatedAt: now,
    createdBy,
    status: 'draft',
  };
};

// Factory functions for each artifact type
export const createDocumentArtifact = (
  title: string,
  createdBy: 'user' | 'agent' = 'user',
  data?: Partial<Pick<DocumentArtifact, 'content' | 'format' | 'citations' | 'sourceMessageId'>>,
): DocumentArtifact => ({
  ...createBaseFields('document', title, createdBy),
  type: 'document',
  content: data?.content ?? '',
  format: data?.format ?? 'markdown',
  citations: data?.citations,
  sourceMessageId: data?.sourceMessageId,
});

export const createEmailArtifact = (
  title: string,
  createdBy: 'user' | 'agent' = 'user',
  data?: Partial<
    Pick<
      EmailArtifact,
      | 'to'
      | 'cc'
      | 'bcc'
      | 'subject'
      | 'body'
      | 'bodyFormat'
      | 'attachments'
      | 'citations'
      | 'sourceMessageId'
    >
  >,
): EmailArtifact => ({
  ...createBaseFields('email', title, createdBy),
  type: 'email',
  to: data?.to ?? [],
  cc: data?.cc,
  bcc: data?.bcc,
  subject: data?.subject ?? '',
  body: data?.body ?? '',
  bodyFormat: data?.bodyFormat ?? 'html',
  attachments: data?.attachments,
  citations: data?.citations,
  sourceMessageId: data?.sourceMessageId,
});

export const createDataArtifact = (
  title: string,
  createdBy: 'user' | 'agent' = 'user',
  data?: Partial<
    Pick<DataArtifact, 'columns' | 'rows' | 'sourceFile' | 'citations' | 'sourceMessageId'>
  >,
): DataArtifact => ({
  ...createBaseFields('data', title, createdBy),
  type: 'data',
  columns: data?.columns ?? [],
  rows: data?.rows ?? [],
  sourceFile: data?.sourceFile,
  citations: data?.citations,
  sourceMessageId: data?.sourceMessageId,
});
